import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { FeatureFlagProvider } from './hooks/useFeatureFlag';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPageRevamped from './pages/DashboardPageRevamped';
import OnboardingPage from './pages/OnboardingPage';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { TemplatesPage } from './features/templates/TemplatesPage';
import { SequencesPage } from './features/sequences/SequencesPage';
import SequenceBuilderPage from './pages/SequenceBuilderPage';
import CompanySearchPage from './features/search/CompanySearchPage';
import PeopleSearchPage from './features/search/pages/PeopleSearchPage';
import { CampaignsPage } from './features/campaigns/CampaignsPage';
import { CreateCampaignPage } from './features/campaigns/pages/CreateCampaignPage';
import { CampaignDetailPage } from './features/campaigns/pages/CampaignDetailPage';
import { ConversationsPage } from './features/conversations/ConversationsPage';
import { ConversationsManagementPage } from './features/conversations/ConversationsManagementPage';
import RevolutionaryConversationsPage from './features/conversations/RevolutionaryConversationsPage';
import { TestIntegration } from './components/TestIntegration';
import { CustomersPage } from './features/customers/CustomersPage';
import { ContractsPage } from './features/contracts/ContractsPage';
import SettingsPage from './pages/SettingsPage';
import ReviewPage from './pages/ReviewPage';
import Navbar from './components/navigation/Navbar';
import { Loader2 } from 'lucide-react';
import { SMSConversations } from './features/sms/SMSConversations';
import { VoicemailCampaigns } from './features/voicemail/VoicemailCampaigns';
import { ComplianceDashboard } from './features/compliance/ComplianceDashboard';
import SMSAnalyticsDashboard from './features/analytics/SMSAnalyticsDashboard';
import VoicemailAnalyticsDashboard from './features/analytics/VoicemailAnalyticsDashboard';
import { ContactAnalyticsDashboard } from './features/analytics/ContactAnalyticsDashboard';
import CostDashboard from './features/billing/CostDashboard';
import { APILogDashboard } from './features/billing/APILogDashboard';
import { APIPricingDashboard } from './features/billing/APIPricingDashboard';
import HelpPage from './pages/HelpPage';
import { WorkflowsPage } from './features/workflows/WorkflowsPage';
import { TraceDashboard } from './features/tracing/TraceDashboard';

// Production monitoring and error tracking
import { setupErrorTracking } from './services/errorTracking';
import { setupPerformanceMonitoring, checkPerformanceThresholds } from './services/performanceMonitoring';

// Add imports for premium components demo
import PremiumShowcase from '@/components/demo/PremiumShowcase';
import ConversationsPagePremium from '@/features/conversations/ConversationsPagePremium';

// Add imports for improved conversations components
import { ImprovedConversationsPage } from './features/conversations/ImprovedConversationsPage';
import { ConversationComparison } from './features/conversations/ConversationComparison';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (previously cacheTime)
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error instanceof Error && 'status' in error && typeof error.status === 'number') {
          if (error.status >= 400 && error.status < 500) {
            return false;
          }
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Layout wrapper for protected pages
const ProtectedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      <Navbar />
      <div className="ml-16 transition-all duration-300">
        {children}
      </div>
    </div>
  );
};

// Onboarding Route component (publicly accessible)
const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Protected Route component without layout
const ProtectedRouteNoLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Check if we have a token in localStorage but auth state isn't fully initialized
  const hasTokenButNotAuthenticated = !isAuthenticated && !isLoading && localStorage.getItem('token');

  if (isLoading || hasTokenButNotAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">
            {hasTokenButNotAuthenticated ? 'Verifying authentication...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

// Public Route component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-neutral-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Error Boundary wrapper for the entire app
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean; error: Error } {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Revolutionary Conversations Interface Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="text-red-500 text-6xl mb-4">🚨</div>
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-neutral-400 mb-6">
              The Revolutionary Conversations Interface encountered an unexpected error.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded transition-colors"
              >
                Go Home
              </button>
            </div>
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-neutral-400 cursor-pointer mb-2">Error Details</summary>
                <pre className="text-red-400 text-xs bg-neutral-900 p-3 rounded overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Production monitoring component
const ProductionMonitoring: React.FC = () => {
  useEffect(() => {
    // Initialize error tracking
    setupErrorTracking();
    
    // Initialize performance monitoring
    setupPerformanceMonitoring();
    
    // Start performance threshold checking
    checkPerformanceThresholds();
    
    // Track page views for Revolutionary Conversations Interface
    const trackPageView = () => {
      if (window.location.pathname === '/conversations') {
        performance.mark('conversations-page-visit');
        
        // Send to analytics (only if analytics URL is configured)
        if (import.meta.env.VITE_ANALYTICS_URL) {
          fetch(`${import.meta.env.VITE_ANALYTICS_URL}/pageview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              page: '/conversations',
              feature: 'revolutionary-conversations-interface',
              timestamp: Date.now(),
              userAgent: navigator.userAgent
            })
          }).catch(console.error);
        }
      }
    };
    
    // Track initial page view
    trackPageView();
    
    // Track navigation changes
    const handleNavigation = () => {
      trackPageView();
    };
    
    window.addEventListener('popstate', handleNavigation);
    
    // Real-time connection monitoring - MUCH LESS AGGRESSIVE
    const monitorConnections = () => {
      // Only check if we have analytics configured to avoid hitting rate limits
      if (!import.meta.env.VITE_ANALYTICS_URL) {
        return;
      }
      
      // Test WebSocket connection health - but don't actually connect
      // Just check if WebSocket is available
      if (typeof WebSocket !== 'undefined') {
        console.log('🔌 WebSocket support available');
      } else {
        console.warn('⚠️ WebSocket not supported');
        // Report connection issues only if analytics is configured
        fetch(`${import.meta.env.VITE_ANALYTICS_URL}/connection-error`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'websocket-unsupported',
            timestamp: Date.now()
          })
        }).catch(console.error);
      }
    };
    
    // Check connections on app start and much less frequently
    monitorConnections();
    const connectionCheckInterval = setInterval(monitorConnections, 10 * 60 * 1000); // Every 10 minutes (instead of 5)
    
    // Memory usage monitoring - MUCH LESS AGGRESSIVE
    const monitorMemory = () => {
      // Only monitor if analytics is configured and memory API is available
      if (!import.meta.env.VITE_ANALYTICS_URL || !('memory' in performance)) {
        return;
      }
      
      const memInfo = (performance as any).memory;
      
      // Only log and report if memory usage is actually high (not every check)
      if (memInfo.usedJSHeapSize > 150 * 1024 * 1024) { // Only if over 150MB (was 100MB)
        console.warn(`⚠️ High memory usage: ${Math.round(memInfo.usedJSHeapSize / 1024 / 1024)}MB`);
        
        // Send memory stats only when there's actually an issue
        fetch(`${import.meta.env.VITE_ANALYTICS_URL}/memory-usage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usedJSHeapSize: memInfo.usedJSHeapSize,
            totalJSHeapSize: memInfo.totalJSHeapSize,
            jsHeapSizeLimit: memInfo.jsHeapSizeLimit,
            timestamp: Date.now(),
            alert: true // Flag this as an alert
          })
        }).catch(console.error);
      }
    };
    
    // Much less frequent memory monitoring
    const memoryCheckInterval = setInterval(monitorMemory, 5 * 60 * 1000); // Every 5 minutes (instead of 30 seconds)
    
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      clearInterval(connectionCheckInterval);
      clearInterval(memoryCheckInterval);
    };
  }, []);
  
  return null;
};

// Wrapper components for route parameters
const SMSAnalyticsRoute: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  return <SMSAnalyticsDashboard campaignId={campaignId || ''} />;
};

const VoicemailAnalyticsRoute: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  return <VoicemailAnalyticsDashboard campaignId={campaignId || ''} />;
};

const ContactAnalyticsRoute: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  return <div className="p-6"><ContactAnalyticsDashboard contactId={contactId || ''} /></div>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <SignupPage />
        </PublicRoute>
      } />

      {/* Special Routes - No Navbar */}
      <Route path="/onboarding" element={
        <OnboardingRoute>
          <OnboardingPage />
        </OnboardingRoute>
      } />

      {/* Protected Routes with Navbar */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPageRevamped />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/analytics/sms/:campaignId" element={
        <ProtectedRoute>
          <SMSAnalyticsRoute />
        </ProtectedRoute>
      } />
      <Route path="/analytics/voicemail/:campaignId" element={
        <ProtectedRoute>
          <VoicemailAnalyticsRoute />
        </ProtectedRoute>
      } />
      <Route path="/analytics/contacts/:contactId" element={
        <ProtectedRoute>
          <ContactAnalyticsRoute />
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute>
          <TemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/review" element={
        <ProtectedRoute>
          <ReviewPage />
        </ProtectedRoute>
      } />
      <Route path="/search" element={<Navigate to="/search/companies" replace />} />
      <Route path="/search/companies" element={
        <ProtectedRoute>
          <CompanySearchPage />
        </ProtectedRoute>
      } />
      <Route path="/search/people" element={
        <ProtectedRoute>
          <PeopleSearchPage />
        </ProtectedRoute>
      } />
      <Route path="/campaigns" element={
        <ProtectedRoute>
          <CampaignsPage />
        </ProtectedRoute>
      } />
      <Route path="/campaigns/create" element={
        <ProtectedRoute>
          <CreateCampaignPage />
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:id/edit" element={
        <ProtectedRoute>
          <CreateCampaignPage />
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:id" element={
        <ProtectedRoute>
          <CampaignDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/sequences" element={
        <ProtectedRoute>
          <SequencesPage />
        </ProtectedRoute>
      } />
      <Route path="/sequences/builder" element={
        <ProtectedRoute>
          <SequenceBuilderPage />
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <CustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/contracts" element={
        <ProtectedRoute>
          <ContractsPage />
        </ProtectedRoute>
      } />
      
      {/* Revolutionary Conversations Interface Routes - REORDERED FOR PROPER MATCHING */}
      
      {/* New Improved Conversations Routes - MOVED TO TOP */}
      <Route path="/conversations/improved" element={
        <ProtectedRoute>
          <ImprovedConversationsPage />
        </ProtectedRoute>
      } />
      <Route path="/conversations/comparison" element={
        <ProtectedRoute>
          <ConversationComparison />
        </ProtectedRoute>
      } />
      
      {/* Test Integration Route */}
      <Route path="/conversations/test" element={
        <ProtectedRoute>
          <TestIntegration />
        </ProtectedRoute>
      } />
      
      {/* Legacy Conversations (for comparison/fallback) */}
      <Route path="/conversations/legacy" element={
        <ProtectedRoute>
          <ConversationsPage />
        </ProtectedRoute>
      } />
      <Route path="/conversations/management" element={
        <ProtectedRoute>
          <ConversationsManagementPage />
        </ProtectedRoute>
      } />
      
      {/* Main conversations route - MOVED TO BOTTOM */}
      <Route path="/conversations" element={
        <ProtectedRoute>
          <RevolutionaryConversationsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sms/conversations" element={
        <ProtectedRoute>
          <SMSConversations />
        </ProtectedRoute>
      } />
      <Route path="/voicemail/campaigns" element={
        <ProtectedRoute>
          <VoicemailCampaigns />
        </ProtectedRoute>
      } />
      <Route path="/compliance" element={
        <ProtectedRoute>
          <ComplianceDashboard />
        </ProtectedRoute>
      } />
      <Route path="/contacts" element={<Navigate to="/customers" replace />} />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/costs" element={<CostDashboard />} />
      <Route path="/costs/api-logs" element={<APILogDashboard />} />
      <Route path="/costs/api-pricing" element={<APIPricingDashboard />} />
      <Route path="/cost-tracking" element={<CostDashboard />} />
      <Route path="/budget-management" element={<CostDashboard />} />
      <Route path="/cost-optimization" element={<CostDashboard />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/workflows" element={
        <ProtectedRoute>
          <WorkflowsPage />
        </ProtectedRoute>
      } />
      <Route path="/traces" element={
        <ProtectedRoute>
          <TraceDashboard />
        </ProtectedRoute>
      } />

      {/* Add these routes: */}
      <Route path="/premium-showcase" element={<PremiumShowcase />} />
      <Route path="/conversations-premium" element={<ConversationsPagePremium />} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <FeatureFlagProvider>
          <AuthProvider>
            <ToastProvider>
              <Router>
                <div className="App">
                  <ProductionMonitoring />
                  <AppRoutes />
                </div>
              </Router>
            </ToastProvider>
          </AuthProvider>
        </FeatureFlagProvider>
        {/* React Query DevTools - only in development */}
        {import.meta.env.DEV && (
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition="bottom-left"
          />
        )}
      </QueryClientProvider>
    </AppErrorBoundary>
  );
};

export default App; 